import { agencyId } from "./config";

type LeadEnquiryType = 0 | 1 | 2; // 0=General, 1=AppraisalRequest, 2=ListingEnquiry
export interface CreateLeadRequest {
  ListingId?: number;
  AutopilotAgencyId?: string;
  RexAgentId?: number;
  NameOfEnquirer?: {
    firstName: string;
    lastName: string;
    title?: string;
    middleName?: string;
    preferredName?: string;
  };
  Email?: string;
  Phone?: { number: string };
  EnquiryType: LeadEnquiryType;
  Note?: string;
}

const DEFAULT_ENDPOINT =
  "https://agentx-apim-prd.azure-api.net/prd-webhooks/rex/CreateLead";
const AU_PHONE_RE = /^(?:\(\d{2}\)\d{8}|\d{2}\d{8}|\d{8}|\d{6})$/;

export interface InitOptions {
  selector?: string; // default: form[data-create-lead="rex"]
  endpoint?: string; // default: Azure APIM endpoint
  timeoutMs?: number; // default: 10000
  onSuccess?: (res: Response) => void;
  onError?: (err: unknown) => void;
}

const bound = new WeakSet<HTMLFormElement>();

export function initWebflowRexLead(opts: InitOptions = {}) {
  const {
    selector = 'form[data-create-lead="rex"]',
    endpoint = DEFAULT_ENDPOINT,
    timeoutMs = 10_000,
    onSuccess,
    onError,
  } = opts;

  const forms = Array.from(
    document.querySelectorAll<HTMLFormElement>(selector)
  );
  console.log("forms", forms);
  forms.forEach((form) => {
    if (bound.has(form)) return;
    bound.add(form);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();

      const done = form.querySelector<HTMLElement>(".w-form-done");
      const fail = form.querySelector<HTMLElement>(".w-form-fail");
      const show = (el?: HTMLElement | null) => {
        if (el) el.style.display = "block";
      };
      const hide = (el?: HTMLElement | null) => {
        if (el) el.style.display = "none";
      };

      hide(done);
      hide(fail);

      try {
        const payload = buildPayloadFromForm(form);
        validatePayload(payload);

        const res = await fetchWithTimeout(
          endpoint,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
          timeoutMs
        );

        if (!res.ok) {
          const msg = await safeText(res);
          throw new Error(
            `CreateLead failed (${res.status}): ${msg || res.statusText}`
          );
        }

        form.reset();
        show(done);
        hide(fail);
        onSuccess?.(res);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unexpected error";
        if (fail)
          fail.innerHTML = `<div role="alert">${escapeHtml(message)}</div>`;
        hide(done);
        show(fail);
        onError?.(err);
      }
    });
  });
}

// Map your Webflow names: First-Name, Last-Name, Email, Phone, Message
function buildPayloadFromForm(form: HTMLFormElement): CreateLeadRequest {
  const fd = new FormData(form);
  const get = (name: string): string | undefined => {
    const raw = fd.get(name);
    return raw ? String(raw).trim() : undefined;
  };

  const firstName = get("First-Name") ?? "";
  const lastName = get("Last-Name") ?? "";
  const email = get("Email");
  const phoneRaw = get("Phone");
  const note = get("Message");

  const urlParams = new URLSearchParams(window.location.search);
  let listingId: number | undefined;
  const addressParam = urlParams.get("address");
  if (addressParam) {
    const firstPart = addressParam.split("-")[0];
    const parsed = Number(firstPart);
    if (!Number.isNaN(parsed)) listingId = parsed;
  }

  const toNum = (v?: string) =>
    v && !Number.isNaN(Number(v)) ? Number(v) : undefined;

  const payload: CreateLeadRequest = {
    ListingId: listingId ?? toNum(form.dataset.listingId),
    RexAgentId: toNum(form.dataset.rexAgentId),
    AutopilotAgencyId: agencyId,
    NameOfEnquirer: { firstName, lastName },
    Email: email,
    Phone: phoneRaw ? { number: phoneRaw.replace(/\D+/g, "") } : undefined,
    EnquiryType: 2, // default ListingEnquiry
    Note: note,
  };

  return payload;
}

function validatePayload(p: CreateLeadRequest) {
  if (!p.NameOfEnquirer?.firstName?.trim())
    throw new Error("First name is required.");
  if (!p.NameOfEnquirer?.lastName?.trim())
    throw new Error("Last name is required.");
  if (!p.Email && !p.Phone?.number)
    throw new Error("Provide at least an email or phone number.");
  if (p.Phone?.number && !AU_PHONE_RE.test(p.Phone.number)) {
    throw new Error(
      "Phone must match AU formats: (##)########, ##########, ########, or ######."
    );
  }
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number
) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(id);
  }
}

async function safeText(res: Response) {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Expose a global for Webflow inline usage
declare global {
  interface Window {
    ReXLead?: { init: (opts?: InitOptions) => void };
  }
}
if (typeof window !== "undefined") {
  console.clear();
  console.log("init");
  window.ReXLead = { init: initWebflowRexLead };
}
